import { FormApi } from 'final-form';
import React, { useContext } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Error as ErrorType } from '../../../api/setup/getApiError';
import { SettingsContext, useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { HorizontalCenterBlock, ShortSettingsBlock } from '../../../styled';
import { FieldSwitch, FormFooter, HeaderTitle } from '../../ui';
import { DescriptionText, DescriptionTitle, DescriptionWrapper, ToggleWrapper } from './style';

type Props = RouteComponentProps & {
  projectId: string;
};

type PasskeyForm = {
  is_passkey_auth_allowed: boolean;
};

const PasskeySettings = ({ history, projectId }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { currentProject, currentUpdateFetching, patchCurrentFineProject } =
    useCurrentProject(projectId);
  const { setCurrentProject } = useContext(SettingsContext);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const backToNavigation = () => history.push({ pathname: navigationPath(projectId) });

  const onSubmit = async (values: PasskeyForm, form: FormApi<PasskeyForm>) => {
    try {
      await patchCurrentFineProject(
        { is_passkey_auth_allowed: values.is_passkey_auth_allowed },
        projectId
      );

      // patchCurrentFineProject sends the partial patch but leaves the cached
      // project untouched, so the navigation card, the tab bar and this form on
      // remount would keep showing the previous value until a full page reload.
      if (currentProject) {
        setCurrentProject({
          ...currentProject,
          is_passkey_auth_allowed: values.is_passkey_auth_allowed
        });
      }

      showSuccessNotify();
    } catch (err) {
      // The backend rejects with 003-102 when enabling passkeys would leave the
      // project with passkey as its only login method. Pass the error through so
      // the publisher reads what to fix instead of a generic failure.
      showErrorNotify(err as ErrorType);

      // Nothing was saved, so put the switch back where the server has it.
      // Leaving it in the rejected position would claim passkeys are on until
      // the page is reloaded, and the fix is on another page anyway.
      // Write the field directly rather than form.reset(): reset goes through
      // re-initialisation, which does not reliably land while the submission is
      // still settling. Deferred because final-form rejects mutations made
      // synchronously inside onSubmit.
      setTimeout(() =>
        form.change('is_passkey_auth_allowed', !!currentProject?.is_passkey_auth_allowed)
      );
    }
  };

  return (
    <div data-test-id="passkey-settings-form">
      <Form
        onSubmit={onSubmit}
        initialValues={{
          is_passkey_auth_allowed: !!currentProject?.is_passkey_auth_allowed
        }}>
        {({ handleSubmit, pristine }) => {
          return (
            <>
              <HorizontalCenterBlock>
                <ShortSettingsBlock>
                  <HeaderTitle
                    title={intl.formatMessage({ id: 'passkey.settings.title' })}
                    subtitle={intl.formatMessage({ id: 'passkey.settings.subtitle' })}
                  />

                  <DescriptionWrapper>
                    <DescriptionTitle>
                      {intl.formatMessage({ id: 'passkey.settings.usage.title' })}
                    </DescriptionTitle>
                    <DescriptionText>
                      {intl.formatMessage({ id: 'passkey.settings.usage.subtitle' })}
                    </DescriptionText>
                  </DescriptionWrapper>

                  <ToggleWrapper>
                    <FieldSwitch
                      name="is_passkey_auth_allowed"
                      label={intl.formatMessage({ id: 'passkey.settings.toggle.title' })}
                      caption={intl.formatMessage({ id: 'passkey.settings.toggle.description' })}
                    />
                  </ToggleWrapper>
                </ShortSettingsBlock>
              </HorizontalCenterBlock>

              <FormFooter
                onSubmit={handleSubmit}
                onCancel={backToNavigation}
                disabled={pristine}
                fetching={currentUpdateFetching}
              />
            </>
          );
        }}
      </Form>
    </div>
  );
};

export default withRouter(PasskeySettings);
